import React, { useEffect, useState } from 'react';
import { cleaningApi, formatApiError } from '../services/api';
import { Button } from './ui/button';
import { CheckCircle, Circle, Sparkle, SkipForward, Play, Clock, User } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * CleaningPanel — Checkliste für Fahrzeugreinigung.
 * Props:
 *  - vehicleId (required)
 *  - onChanged (fn) — nach Status-Änderungen (z.B. um Fahrzeugliste zu refetchen)
 */
export default function CleaningPanel({ vehicleId, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [action, setAction] = useState(null);
  const [editingNoteKey, setEditingNoteKey] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await cleaningApi.getForVehicle(vehicleId);
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vehicleId]);

  const start = async () => {
    setAction('start');
    try {
      await cleaningApi.start(vehicleId);
      toast.success('Reinigung gestartet');
      await load();
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAction(null); }
  };

  const toggle = async (item, checked) => {
    setBusyKey(item.key);
    try {
      const r = await cleaningApi.toggleItem(vehicleId, item.key, { checked });
      await load();
      if (r.data.auto_completed) {
        toast.success('Reinigung automatisch abgeschlossen', {
          description: 'Alle Pflicht-Punkte erledigt · Fahrzeug ist wieder verfügbar.',
        });
      }
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyKey(null); }
  };

  const saveNote = async (item) => {
    try {
      await cleaningApi.toggleItem(vehicleId, item.key, { notes: noteDraft });
      setEditingNoteKey(null);
      setNoteDraft('');
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const complete = async () => {
    setAction('complete');
    try {
      await cleaningApi.complete(vehicleId);
      toast.success('Reinigung abgeschlossen');
      await load();
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAction(null); }
  };

  const skip = async () => {
    if (!window.confirm('Reinigung wirklich überspringen? Fahrzeug wird direkt verfügbar.')) return;
    setAction('skip');
    try {
      await cleaningApi.skip(vehicleId);
      toast.success('Reinigung übersprungen');
      await load();
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAction(null); }
  };

  if (loading) return <div className="card-swiss p-4 h-32 skeleton" />;
  if (!data) return null;

  const inProgress = data.in_progress;
  const items = (data.cleaning_state?.items || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const progress = data.progress;
  const percent = progress ? Math.round((progress.done / Math.max(1, progress.total)) * 100) : 0;

  return (
    <div className="card-swiss p-4 space-y-3" data-testid="cleaning-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-sm bg-blue-100 flex items-center justify-center">
            <Sparkle weight="bold" size={16} className="text-blue-700" />
          </div>
          <div>
            <p className="font-bold text-[#111418]">Fahrzeug-Reinigung</p>
            {inProgress ? (
              <p className="text-xs text-[#525866]">
                Gestartet {data.cleaning_state?.started_at ? new Date(data.cleaning_state.started_at).toLocaleString('de-DE') : '—'}
                {data.cleaning_state?.started_by_name && ` · ${data.cleaning_state.started_by_name}`}
                {data.cleaning_state?.trigger === 'check_out' && ' · nach Rückgabe'}
              </p>
            ) : (
              <p className="text-xs text-[#525866]">
                {data.cleaning_state?.status === 'done'
                  ? `Abgeschlossen ${data.cleaning_state?.completed_at ? new Date(data.cleaning_state.completed_at).toLocaleString('de-DE') : ''}${data.cleaning_state?.skipped ? ' (übersprungen)' : ''}`
                  : 'Keine Reinigung aktiv'}
              </p>
            )}
          </div>
        </div>

        {!inProgress && (
          <Button size="sm" variant="outline" onClick={start} disabled={action === 'start'} data-testid="cleaning-start-btn">
            <Play weight="bold" size={12} className="mr-1"/> Reinigung starten
          </Button>
        )}
      </div>

      {inProgress && (
        <>
          {/* Progress */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-[#525866]">
                {progress?.required_done ?? 0} / {progress?.required_total ?? 0} Pflicht ·
                {' '}{progress?.done ?? 0} / {progress?.total ?? 0} gesamt
              </span>
              <span className="font-semibold">{percent}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${percent}%` }} />
            </div>
          </div>

          {/* Items */}
          <ul className="space-y-1.5">
            {items.map((it) => (
              <li key={it.key} className={`border border-[#E2E4E9] rounded p-2 ${it.checked ? 'bg-emerald-50/50' : 'bg-white'}`} data-testid={`cleaning-item-${it.key}`}>
                <div className="flex items-start gap-2">
                  <button
                    onClick={() => toggle(it, !it.checked)}
                    disabled={busyKey === it.key}
                    className="mt-0.5 flex-shrink-0"
                    data-testid={`cleaning-toggle-${it.key}`}
                  >
                    {it.checked
                      ? <CheckCircle weight="fill" size={22} className="text-emerald-600"/>
                      : <Circle weight="regular" size={22} className="text-gray-400 hover:text-gray-600"/>}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <span className={`text-sm ${it.checked ? 'text-[#525866] line-through' : 'font-semibold text-[#111418]'}`}>
                        {it.label}
                      </span>
                      {it.required && !it.checked && (
                        <span className="text-[10px] text-orange-700 bg-orange-50 px-1.5 py-0.5 rounded font-semibold">Pflicht</span>
                      )}
                    </div>
                    {it.checked && it.checked_by_name && (
                      <p className="text-[11px] text-[#8A94A6] mt-0.5 flex items-center gap-2">
                        <User size={10}/> {it.checked_by_name}
                        <Clock size={10}/> {it.checked_at ? new Date(it.checked_at).toLocaleTimeString('de-DE', {hour:'2-digit',minute:'2-digit'}) : ''}
                      </p>
                    )}
                    {/* Notiz */}
                    {editingNoteKey === it.key ? (
                      <div className="mt-1 flex gap-1">
                        <input
                          type="text"
                          value={noteDraft}
                          onChange={(e) => setNoteDraft(e.target.value)}
                          placeholder="Notiz eingeben…"
                          className="flex-1 px-2 py-1 text-xs border border-[#E2E4E9] rounded"
                          autoFocus
                          onKeyDown={(e) => { if (e.key === 'Enter') saveNote(it); if (e.key === 'Escape') setEditingNoteKey(null); }}
                          data-testid={`cleaning-note-input-${it.key}`}
                        />
                        <Button size="sm" onClick={() => saveNote(it)} className="btn-primary h-7 text-xs">OK</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingNoteKey(null)} className="h-7 text-xs">Abbr.</Button>
                      </div>
                    ) : it.notes ? (
                      <p className="text-[11px] text-[#111418] mt-1 bg-[#F7F8FA] rounded px-1.5 py-0.5 cursor-pointer" onClick={() => { setEditingNoteKey(it.key); setNoteDraft(it.notes || ''); }}>
                        📝 {it.notes}
                      </p>
                    ) : (
                      <button onClick={() => { setEditingNoteKey(it.key); setNoteDraft(''); }} className="text-[11px] text-[#8A94A6] hover:text-[#111418] mt-1" data-testid={`cleaning-note-add-${it.key}`}>
                        + Notiz
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex gap-2 pt-2 border-t border-[#E2E4E9]">
            <Button size="sm" className="btn-primary" onClick={complete} disabled={action === 'complete'} data-testid="cleaning-complete-btn">
              <CheckCircle weight="bold" size={14} className="mr-1"/> Reinigung abschließen
            </Button>
            <Button size="sm" variant="outline" onClick={skip} disabled={action === 'skip'} data-testid="cleaning-skip-btn">
              <SkipForward weight="bold" size={14} className="mr-1"/> Überspringen
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
